import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { FC, useEffect, useState } from 'react';

import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
} from 'react-dates';

import {
  DateType,
  getCurrentDate,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import { renderEvents } from '@components/views/hotel/detail/components/room-selection/components/AvailableRoomDates/helpers';
import { ChevronLeft, ChevronRight } from '@assets/icons';

import {
  DatePickerWrapper,
  NavNextButton,
  NavPrevButton,
} from '../DatePickerStyle';
import { renderNavButton } from '../renderNavButton';
import { useCalendarEventsDates } from '../useCalendarEventsDates';

type RequiredProps = 'focusedInput' | 'onFocusChange' | 'initialVisibleMonth';

export type DayPickerRangeControllerProps = Omit<
  DayPickerRangeControllerShape,
  RequiredProps
> &
  Partial<Pick<DayPickerRangeControllerShape, RequiredProps>> & {
    initialFocusedInput?: 'startDate' | 'endDate' | null;
  };

const DateRangePicker: FC<DayPickerRangeControllerProps> = ({
  initialVisibleMonth = null,
  onDatesChange,
  startDate,
  initialFocusedInput,
  ...props
}) => {
  const { events } = useCalendarEventsDates();
  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >(initialFocusedInput || 'startDate');

  const [drawerContentHeight, setDrawerContentHeight] = useState<number | null>(
    null
  );

  const findDrawerContentHeight = () => {
    if (typeof window !== 'undefined') {
      const el = document.getElementsByClassName('setur-drawer-body')[0];
      return el?.clientHeight;
    }
  };

  useEffect(() => {
    const updateHeight = () => {
      setDrawerContentHeight(findDrawerContentHeight() as number);
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return (
    <DatePickerWrapper contentHeight={drawerContentHeight as number}>
      <DayPickerRangeController
        verticalBorderSpacing={3}
        initialVisibleMonth={initialVisibleMonth}
        focusedInput={props.focusedInput || focusedInput}
        onFocusChange={(nextFocusedInput) => {
          setFocusedInput(nextFocusedInput);
        }}
        startDate={startDate}
        renderDayContents={(day) => renderEvents(day, events)}
        onDatesChange={onDatesChange}
        {...props}
        renderMonthText={undefined}
      />
    </DatePickerWrapper>
  );
};

export const dateRangePickerDefaultProps: DayPickerRangeControllerProps = {
  startDate: null,
  endDate: null,
  daySize: 33,
  onDatesChange: () => false,
  // day presentation and interaction related props
  minimumNights: 1,
  isOutsideRange: (day: DateType) =>
    !isInclusivelyAfterDay(day, getCurrentDate()),
  enableOutsideDays: false,
  daysViolatingMinNightsCanBeClicked: false,

  // calendar presentation and interaction related props
  orientation: 'horizontal',
  withPortal: false,
  numberOfMonths: 2,
  keepOpenOnDateSelect: false,
  isRTL: false,

  // navigation related props
  renderNavPrevButton: renderNavButton(NavPrevButton, ChevronLeft),
  renderNavNextButton: renderNavButton(NavNextButton, ChevronRight),

  // internationalization
  monthFormat: 'MMMM YYYY',

  hideKeyboardShortcutsPanel: true,
};

DateRangePicker.defaultProps = dateRangePickerDefaultProps;
export default DateRangePicker;
